import { QueryClient } from '@tanstack/react-query';
import type { AppRouter } from '@teo/backend';
import { defined } from '@teo/types';
import {
  TRPCClientError,
  type TRPCLink,
  createTRPCClient,
  createWSClient,
  httpBatchLink,
  splitLink,
  wsLink,
} from '@trpc/client';
import { observable } from '@trpc/server/observable';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import superjson from 'superjson';

import { getDeviceFingerprint } from '../utils/deviceFingerprint';
import logger, { traceId } from '../utils/logger';
import {
  clearAuthTokensIfRefreshTokenMatches,
  invalidateAuthSession,
  isConfirmedSessionFailure,
  isUsableAuthToken,
  storeAuthTokens,
} from './authSession';
import { trpcErrorSanitizerLink } from './trpcErrorSanitizer';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

/**
 * Vanilla tRPC client for token refresh operations
 * This client does NOT include the token refresh link to avoid circular dependencies
 */
const refreshClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: '/trpc',
      transformer: superjson,
      headers: () => ({
        'x-device-fingerprint': getDeviceFingerprint(),
        'x-trace-id': traceId,
      }),
    }),
  ],
});

/**
 * Token refresh state to prevent multiple simultaneous refresh attempts
 */
let isRefreshing = false;
let refreshPromise: Promise<{ token: string; refreshToken?: string } | null> | null = null;

/**
 * Attempt to refresh the access token using the refresh token
 * @returns New access token or null if refresh failed
 */
async function refreshAccessToken(): Promise<{ token: string; refreshToken?: string } | null> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    try {
      if (!refreshToken) {
        logger.info('No refresh token available for access token refresh');
        return null;
      }

      logger.info({ hasRefreshToken: true }, 'Attempting access token refresh');

      // Use the vanilla tRPC client (without token refresh link) to avoid circular dependency
      const result = await refreshClient.auth.refreshToken.mutate({
        refreshToken,
      });

      if (result?.token) {
        storeAuthTokens(
          {
            token: result.token,
            ...defined({ refreshToken: result.refreshToken }),
          },
          'refresh-success',
        );
        logger.info(
          {
            refreshTokenRotated: Boolean(result.refreshToken),
          },
          'Access token refreshed successfully',
        );
        return {
          token: result.token,
          ...defined({ refreshToken: result.refreshToken }),
        };
      }

      return null;
    } catch (error) {
      const confirmedSessionFailure = isConfirmedSessionFailure(error);
      logger.warn(
        {
          confirmedSessionFailure,
          errorMessage: error instanceof Error ? error.message : 'Unknown refresh error',
        },
        'Access token refresh failed',
      );

      if (confirmedSessionFailure && refreshToken) {
        const clearedCurrentSession = clearAuthTokensIfRefreshTokenMatches(
          refreshToken,
          'refresh-rejected',
          {
            httpStatus: 401,
          },
        );
        if (clearedCurrentSession) {
          invalidateAuthSession(
            'refresh-rejected',
            {
              httpStatus: 401,
            },
            {
              clearTokens: false,
            },
          );
        } else {
          logger.info(
            {
              httpStatus: 401,
            },
            'Refresh rejection did not clear auth session because a newer refresh token exists',
          );
        }
      } else if (confirmedSessionFailure) {
        logger.info(
          {
            httpStatus: 401,
          },
          'Refresh rejection could not clear auth session because no refresh token was available',
        );
        invalidateAuthSession('refresh-rejected-no-refresh-token', {
          httpStatus: 401,
        });
      }

      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Custom tRPC link that intercepts 401 errors and attempts token refresh
 */
const tokenRefreshLink: TRPCLink<AppRouter> = () => {
  return ({ next, op }) => {
    return observable((observer) => {
      let currentSubscription = next(op).subscribe({
        next(value) {
          observer.next(value);
        },
        error(err) {
          // Check if error is a 401 Unauthorized
          if (err instanceof TRPCClientError && err.data?.httpStatus === 401) {
            logger.info(
              {
                path: op.path,
                type: op.type,
              },
              'Received 401 from tRPC operation, attempting token refresh',
            );

            // Unsubscribe from the failed operation
            currentSubscription.unsubscribe();

            refreshAccessToken()
              .then((session) => {
                if (session?.token) {
                  // Retry the original operation with new token
                  logger.info(
                    {
                      path: op.path,
                      type: op.type,
                    },
                    'Retrying tRPC operation after token refresh',
                  );
                  currentSubscription = next(op).subscribe({
                    next(value) {
                      observer.next(value);
                    },
                    error(retryErr) {
                      observer.error(retryErr);
                    },
                    complete() {
                      observer.complete();
                    },
                  });
                } else {
                  // Refresh was not successful. Keep the original request error so the UI
                  // can surface it without forcing a logout on transient backend/network failures.
                  observer.error(err);
                }
              })
              .catch((refreshError) => {
                logger.error('Token refresh error:', refreshError);
                observer.error(err);
              });
          } else {
            observer.error(err);
          }
        },
        complete() {
          observer.complete();
        },
      });

      return () => currentSubscription.unsubscribe();
    });
  };
};

const getWsUrl = () => {
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProtocol}//${window.location.host}/trpc/ws`;
};

const wsClient = createWSClient({
  url: getWsUrl(),
  connectionParams: async () => {
    const token = localStorage.getItem('authToken');
    const deviceFingerprint = getDeviceFingerprint();

    // Not verification — the backend is the authority and holds the keys. This
    // only avoids opening a socket with something already known to be junk.
    if (token && !isUsableAuthToken(token)) {
      invalidateAuthSession('ws-unusable-token');
      return {
        'x-device-fingerprint': deviceFingerprint,
        'x-trace-id': traceId,
        'user-agent': navigator.userAgent,
      };
    }

    return token
      ? {
          authorization: `Bearer ${token}`,
          'x-device-fingerprint': deviceFingerprint,
          'x-trace-id': traceId,
          'user-agent': navigator.userAgent,
        }
      : {
          'x-device-fingerprint': deviceFingerprint,
          'x-trace-id': traceId,
          'user-agent': navigator.userAgent,
        };
  },
  keepAlive: {
    enabled: true,
    intervalMs: 30000, // Send ping every 30 seconds
    pongTimeoutMs: 5000, // Wait 5 seconds for pong response
  },
  lazy: {
    enabled: true, // Connect only when subscriptions are active
    closeMs: 0, // Don't close connection when not in use
  },
  retryDelayMs: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s max
});

const trpcClient = createTRPCClient<AppRouter>({
  links: [
    // Keep this outermost so it sanitizes initial transport errors, errors
    // re-emitted when refresh fails, and errors from the retried operation.
    trpcErrorSanitizerLink,
    // Token refresh link intercepts 401 errors before they reach the UI.
    tokenRefreshLink,
    // Split link for HTTP vs WebSocket
    splitLink({
      condition(op) {
        return op.type === 'subscription';
      },
      true: wsLink<AppRouter>({
        client: wsClient,
        transformer: superjson,
      }),
      false: httpBatchLink({
        url: '/trpc',
        transformer: superjson,
        headers: () => {
          const token = localStorage.getItem('authToken');
          const headers: Record<string, string> = {
            'x-device-fingerprint': getDeviceFingerprint(),
            'x-trace-id': traceId,
          };
          if (token) {
            // Must accept a WorkOS token as well as one of ours. Gating this on
            // our own audience dropped the header for every AuthKit session —
            // WorkOS tokens carry no `aud` — which read as a 401 and bounced
            // the user back to a login that immediately succeeded again.
            if (!isUsableAuthToken(token)) {
              invalidateAuthSession('http-unusable-token');
            } else {
              headers.authorization = `Bearer ${token}`;
            }
          }
          return headers;
        },
      }),
    }),
  ],
});

export { trpcClient };

export const trpc = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
});
